import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col overflow-hidden bg-[#022a51] p-10 text-white lg:flex">
        <img src="/mokora-white.png" alt="Mokora" className="relative z-10 h-10 w-auto self-start" />
        <div className="relative z-10 flex flex-1 flex-col justify-center">
          <h1 className="font-heading text-5xl font-extrabold leading-none tracking-tight">Timesheet Tool</h1>
          <div className="mt-6 h-1.5 w-16 rounded-full bg-[hsl(var(--accent))]" />
        </div>
        <div className="relative z-10 font-mono text-xs text-slate-400/70">© 2026 Mokora</div>
        <img src="/mokora-icon.png" alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-20 -right-20 h-96 w-96 opacity-[0.06]" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 lg:hidden">
            <img src="/mokora-navy.png" alt="Mokora" className="h-9 w-auto dark:hidden" />
            <img src="/mokora-white.png" alt="Mokora" className="hidden h-9 w-auto dark:block" />
          </div>

          {sent ? (
            <div data-testid="forgot-sent" className="space-y-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                <MailCheck className="h-5 w-5" />
              </div>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight">Check your email</h2>
              <p className="text-sm text-muted-foreground">
                If an account exists for <strong>{email}</strong>, we've sent a link to reset your password. It expires in 1 hour.
              </p>
              <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#022a51] hover:underline dark:text-sky-300">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight">Forgot password?</h2>
              <p className="mt-1 text-sm text-muted-foreground">Enter your email and we'll send you a reset link.</p>

              {error && (
                <div data-testid="forgot-error" className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
                  {error}
                </div>
              )}

              <form data-testid="forgot-form" onSubmit={submit} className="mt-5 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="f-email">Email</Label>
                  <Input id="f-email" data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" />
                </div>
                <Button data-testid="forgot-submit-btn" type="submit" disabled={loading} className="w-full">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send reset link
                </Button>
              </form>

              <Link to="/login" data-testid="back-to-login-link" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#022a51] hover:underline dark:text-sky-300">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
